import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";

import DataProtectionSoftwareService from "src/modules/data-protection/apis/DataProtectionSoftwareService";
import CeConformityPdfExport from "../index";

export const CE_CONFORMITY_EXPORT_QUERY_KEY =
    "data-protection-software-conformity-export";

const buildExportFileName = (data, softwareId) => {
    const rawName = data?.software?.name || softwareId || "export";
    const safeName = String(rawName)
        .replace(/[^\w\-]+/g, "_")
        .replace(/_+/g, "_")
        .replace(/^_|_$/g, "");

    return `CE_Conformity_Assessment_${safeName || softwareId || "export"}.pdf`;
};

const revokeObjectUrl = (url) => {
    if (url) {
        URL.revokeObjectURL(url);
    }
};

const useExportCeConformityPdf = ({
    t,
    softwareId,
    organization,
    user,
}) => {
    const [exportPDFInprocess, setExportPDFInprocess] = useState(false);
    const [exportPDFObjectUrl, setExportPDFObjectUrl] = useState(null);

    useEffect(() => {
        return () => {
            revokeObjectUrl(exportPDFObjectUrl);
        };
    }, [exportPDFObjectUrl]);

    const exportQuery = useQuery({
        queryKey: [CE_CONFORMITY_EXPORT_QUERY_KEY, softwareId],
        queryFn: async () => {
            setExportPDFInprocess(true);
            setExportPDFObjectUrl((previousUrl) => {
                revokeObjectUrl(previousUrl);
                return null;
            });

            const service = DataProtectionSoftwareService.getInstance();
            const data = await service.fetchConformityExport(softwareId);

            // Empty/partial packages are still valid for PDF generation.
            return data && typeof data === "object" ? data : {};
        },
        enabled: false,
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onSuccess: async (data) => {
            try {
                const exportData =
                    data && typeof data === "object" && !Array.isArray(data)
                        ? data
                        : {};

                const blob = await pdf(
                    React.createElement(CeConformityPdfExport, {
                        data: exportData,
                        organization: organization || {},
                        user: user || {},
                    })
                ).toBlob();

                saveAs(blob, buildExportFileName(exportData, softwareId));

                const nextUrl = URL.createObjectURL(blob);
                setExportPDFObjectUrl((previousUrl) => {
                    revokeObjectUrl(previousUrl);
                    return nextUrl;
                });
            } catch (error) {
                if (process.env.NODE_ENV === "development") {
                    console.error(error);
                }

                toast(
                    t(
                        "An error occurred while generating the CE Conformity PDF."
                    ),
                    {
                        type: "error",
                    }
                );

                setExportPDFInprocess(false);
                setExportPDFObjectUrl((previousUrl) => {
                    revokeObjectUrl(previousUrl);
                    return null;
                });
            }
        },
        onError: (error) => {
            if (process.env.NODE_ENV === "development") {
                console.error(error);
            }

            toast(
                t(
                    "An error occurred while retrieving the CE Conformity export data."
                ),
                {
                    type: "error",
                }
            );

            setExportPDFInprocess(false);
            setExportPDFObjectUrl((previousUrl) => {
                revokeObjectUrl(previousUrl);
                return null;
            });
        },
    });

    const handleExport = () => {
        if (!softwareId || exportPDFInprocess) {
            return;
        }

        exportQuery.refetch();
    };

    const clearExport = () => {
        setExportPDFInprocess(false);
        setExportPDFObjectUrl((previousUrl) => {
            revokeObjectUrl(previousUrl);
            return null;
        });
    };

    return {
        exportPDFInprocess,
        exportPDFObjectUrl,
        handleExport,
        clearExport,
        isExporting: exportQuery.isFetching,
    };
};

export default useExportCeConformityPdf;
